import videojs from 'video.js';
import Quality from './quality.js';
import QualityList from './quality-list.js';
import NativeQualityList from './native-quality-list.js';
import { checkNativeHLSSupport } from './utils.js';
import { NativeQualityHandler } from './native-quality-handler.js';

const hlsHadler = videojs.VhsHandler || videojs.HlsHandler;

/**
 * 
 * @extends EventTarget
 */
class QualityHandler extends videojs.EventTarget {
  constructor(player, options) {
    super();
    this.native_quality_handler;
    this.player_ = player;
    this.options = options;
    this.qualities = !('MediaSource' in window) && checkNativeHLSSupport()
      ? new NativeQualityList(options)
      : new QualityList(options);
    this.current_quality_ = Array.from(this.qualities)[0];
    this.start_playing = true;
    this.only_low_qulity = false;
    this.updateAudioTracksOnChange();
    this.seekToCurrentTimeOnChange();
  }

  get supported() {
    if (this.player_.techName_ != 'Html5') return false;
    if (this.player_.tech_.sourceHandler_ && 
      this.player_.tech_.sourceHandler_.constructor == hlsHadler) return 'hls-contrib';
    if (this.player_.tech_.sourceHandler_ && this.player_.hls_) return 'hls.js';
    if (checkNativeHLSSupport()) return 'native';
    return false;
  }

  get current_quality() {
    return this.current_quality_;
  }

  changeCurrentQuality(representation) {
    const getAutoQuality = () => {
      for (let quality of this.qualities) {
        if (quality.auto) {
          return quality;
        }
      }
    };
    const getQualityByRepresentation = () => {
      for (let quality of this.qualities) {
        if (quality.eq(representation) && this.current_quality_ != quality) {
          return quality;
        }
      }
    };

    let newQuality = representation
      ? getQualityByRepresentation()
      : getAutoQuality();

    if (!newQuality) return;

    this.current_quality_ = newQuality;
    this.trigger('change');
  }

  forceLowQuality() {
    if (this.supported === 'native') return;
    this.only_low_qulity = true;
    this.initQualitiesFromHLSJS();
    this.trigger('change_selected');
  }
  
  processQualities() {
    if (this.supported == 'hls-contrib') {
      this.qualities.clear();
      this.qualities.on('change_selected', () => {
        let auto = this.qualities.isAuto;
        for (let quality of this.qualities) {
          quality.representation && quality.representation.enabled(auto || quality.selected);
        }
      });
    } else if (this.supported == 'hls.js') {
      let hls = this.player_.hls_;
      const HLS_EVENTS = hls.constructor.Events;

      hls.on(HLS_EVENTS.MANIFEST_LOADED, () => this.initQualitiesFromHLSJS());
      hls.on(HLS_EVENTS.LEVEL_SWITCHED, (_event, data) => {
        // При переключении проигрываемого качества, выполняем:
        //  * обновление элементов управления качествами
        this.changeCurrentQuality(hls.levels[data.level]);
        //  * сохранение текущего проигрываемого качества, выбранного в автоматическом режиме
        this.saveCurrentAutoQuality();
      });

      // Создаем колбэк, который будет вызываться при смене нескольких первых чанков
      const rpf = () => {
        if (this.disableStartQuality()) {
          hls.off(HLS_EVENTS.FRAG_CHANGED, rpf);
        }
      };
      hls.on(HLS_EVENTS.FRAG_CHANGED, rpf);

      // Устанавливаем обработчики событий смены качества
      this.qualities.on('change_forced', () => this.switchHlsQuality('currentLevel'));
      this.qualities.on('change_selected', () => this.switchHlsQuality('nextLevel'));

      // Заполняем меню качеств
      this.initQualitiesFromHLSJS();
    } else if (this.supported == 'dash') {
      this.qualities.clear();
      this.ready_ = false;
      this._hls_playlist_controller = this.player_.tech_.sourceHandler_.masterPlaylistController_;
      this._original_select_fn = this._hls_playlist_controller.selectPlaylist;
      this._hls_playlist_controller.selectPlaylist = () => {
        if (!this.ready_) {
          this.initQualitiesFromDash();
          this.ready_ = true;
        }
        let playlist = this.qualities.getSelectedPlaylist() || this._original_select_fn();
        this.changeCurrentQuality(playlist);
        return playlist;
      };
    } else if (this.supported === 'native') {
      if (this.native_quality_handler === undefined) {
        let options = videojs.mergeOptions(
          this.options,
          {
            qualities: this.qualities,
            changeCurrentQuality: (representation) => this.changeCurrentQuality(representation)
          }
        );
        this.native_quality_handler = new NativeQualityHandler(this.player_, options);
        this.trigger('native_quality_handler/created');
      }
      this.native_quality_handler.update();
      // this.start_playing в нативной реализации нигде не используется. Изменяется для порядка.
      this.start_playing = false;
    }
  }

  switchHlsQuality(level_property) {
    // В случаях, когда происходят изменения качества, логика start_playing уже не нужна
    this.start_playing = false;
    const hls = this.player_.hls_;
    const quality = this.qualities.selectedQuality;
    const level = quality.auto ? -1 : hls.levels.findIndex(level => quality.eq(level));
    // Установка значения currentLevel во время воспроизведения приведет к остановке видео,
    // очистке буфера и загрузке нужного качества. Поэтому устанавливать значение currentLevel
    // нужно, если видео не проигрывается, чтобы не возникло никаких артефактов
    if (level_property === 'currentLevel' && !this.player_.paused()) {
      level_property = 'nextLevel';
    }
    hls[level_property] = level;
  }

  /**
   * Функция для включения автовыбора качества после старта с запомненного качества
   */
  disableStartQuality() {
    const hls = this.player_.hls_;
    const frag = hls.streamController.fragPlaying;
    // было проиграно недостаточное количество чанков
    if (!frag || frag.sn < this.options.maxFragments) {
      return;
    }

    this.start_playing = false;
    const user_quality = this.qualities.getUserPreferredQuality();
    if (!user_quality || user_quality.auto) {
      hls.nextLevel = -1;
    }
    return true;
  }

  /**
   * Сохранение в localStorage качества, которое было выбрано плеером на основании пропускной
   * способности пользователя
   */
  saveCurrentAutoQuality() {
    let hls = this.player_.hls_;

    if (!hls.autoLevelEnabled || this.start_playing) {
      return;
    }

    // Если проигрывается Auto, то сохраняем качество
    let level = hls.levels[hls.currentLevel];
    let quality = this.qualities.list.find(q => q.eq(level));
    this.qualities.saveAutoQuality(quality);
  }

  initQualitiesFromHLSJS() {
    const hls = this.player_.hls_;
    let levels = hls.levels;
    if (!levels) return;
    this.qualities.clear();
    if (this.only_low_qulity) {
      const low_level = levels.sort((a, b) => a.bitrate - b.bitrate)[0];
      while (hls.levels.length > 1) {
        const del_index = hls.levels.findIndex(level => level != low_level);
        hls.removeLevel(del_index);
      }
      levels = [low_level];
    }
    for (const level of levels) {
      this.qualities.addQuality(new Quality(level));
    }
    if (!this.only_low_qulity) this.setStartedQuality();
    this.trigger('qualities_initialized');
  }

  /**
   * Использование наиболее подходящего качества для старта проигрывания
   */
  setStartedQuality() {
    const hls = this.player_.hls_;
    this.start_playing = true;
    if (!this.options.remember_quality) {
      this.qualities.clearAutoQuality();
    }
    // последнее запомненное качество используем для начала проигрывания
    const user_quality = this.qualities.getUserPreferredQuality();
    const last_quality = this.qualities.getLastAutoQuality();
    if (user_quality) {
      this.qualities.selectQuality(user_quality)
    }
    const quality = user_quality && !user_quality.auto ? user_quality : last_quality;
    const level = !quality || quality.auto ? -1 : hls.levels.findIndex(lvl => quality.eq(lvl));
    hls.startLevel = hls.nextLevel = level;
  }

  initQualitiesFromDash() {
    let now = +new Date();
    this.player_.tech_.sourceHandler_.playlists.master.playlists.filter((playlist) => 
      typeof playlist.excludeUntil !== 'undefined' ? now >= playlist.excludeUntil : true
    ).sort(videojs.Hls.comparePlaylistBandwidth)
    .forEach((playlist) => 
      this.qualities.addQuality(new Quality(playlist))
    );
  }

  /**
   * hls-contrib api
  */

  get hls_quality_levels() {
    let levels = [];
    levels.dispose = () => {
      levels.splice(0, levels.length);
      this.qualities.clear();
    };
    levels.addQualityLevel = representation => {
      this.qualities.addQuality(new Quality(representation));
      levels.push(representation);
    };
    levels.trigger = event => {
      if (event.type == 'change') {
        let representation = levels[event.selectedIndex];
        if (representation) this.changeCurrentQuality(representation);
      }
    };
    
    return levels;
  }

  updateAudioTracksOnChange() {
    this.on('change', () => {
      let audioTrackButton = this.player_.controlBar.audioTrackButton;
      if (!audioTrackButton || !audioTrackButton.items.length) {
        return;
      }
      audioTrackButton.items.forEach((item) => {
        if (!item.track || !item.track.properties_) {
          item.hide();
          return;
        };
        if (item.track.properties_.groupId === this.current_quality.representation.attrs.AUDIO) {
          item.show();
        } else {
          item.hide();
        }
      });
    });
  }

  /* Делаем сик по времени на тоже самое время, чтобы не ломался
   * UI из-за возможного изменения ширины при выборе другого качества.
   * Например: Когда переключаемся с "720p" на "UHD, 50 FPS"
   * полоса прогресса остается на том же месте, а не на фактическом,
   * при том, что её контейнер растягивается/сжимается в это время. */
  seekToCurrentTimeOnChange() {
    this.on('change', () => {
      const liveDisplay = this.player_.getChild('LiveDisplay');

      if (liveDisplay) {
        liveDisplay.seekToCurrentTime();
        return;
      }

      const currentTime = this.player_.currentTime();
      this.player_.currentTime(currentTime);
    });
  }
}

export default QualityHandler;
